import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const REFRESH_KEY = 'refresh_token';
const ACCESS_KEY = 'auth_token';
// Non va cancellato al logout: identifica il browser, così il server revoca solo la sessione di questo dispositivo.
const DEVICE_KEY = 'device_id';

export const getDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (_) {
    return undefined;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const resolveApiBaseUrl = () => {
  if (typeof process !== 'undefined' && process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location || {};

    if (!protocol || !hostname) {
      return 'http://localhost:3000/api';
    }

    if (protocol === 'http:' || protocol === 'https:') {
      const port = window.location.port
        ? `:${window.location.port}`
        : protocol === 'https:'
          ? ''
          : window.location.port || '';

      return `${protocol}//${hostname}${port}/api`;
    }
  }

  return 'http://localhost:3000/api';
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(ACCESS_KEY));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshInFlight = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const clearLocalAuth = useCallback(() => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setToken(null);
    setCurrentUser(null);
  }, []);

  const persistTokens = useCallback((access, refresh) => {
    if (access) {
      localStorage.setItem(ACCESS_KEY, access);
      setToken(access);
    }
    if (refresh) {
      localStorage.setItem(REFRESH_KEY, refresh);
    }
  }, []);

  const tryRefresh = useCallback(async () => {
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) return null;

    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }

    refreshInFlight.current = (async () => {
      try {
        const baseURL = resolveApiBaseUrl();
        const response = await axios.post(
          `${baseURL}/auth.php?action=refresh`,
          { refresh_token: refresh },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Platform': 'web',
            },
          }
        );
        const access = response.data?.access_token || response.data?.token;
        const newRefresh = response.data?.refresh_token;
        if (!access) return null;
        persistTokens(access, newRefresh || refresh);
        return access;
      } catch (e) {
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    return refreshInFlight.current;
  }, [persistTokens]);

  // Create API instance with useMemo to prevent unnecessary recreations
  const api = useMemo(() => {
    const baseURL = resolveApiBaseUrl();

    const instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Platform': 'web',
      }
    });

    instance.interceptors.request.use((config) => {
      const latest = tokenRef.current || localStorage.getItem(ACCESS_KEY);
      if (latest) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${latest}`;
      }
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers) {
          if (typeof config.headers.delete === 'function') {
            config.headers.delete('Content-Type');
            config.headers.delete('content-type');
          } else {
            delete config.headers['Content-Type'];
            delete config.headers['content-type'];
          }
        }
      }
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        const isAuthAction = (original.url || '').includes('auth.php')
          && /action=(login|refresh|logout)/.test(original.url || '');

        if (
          error.response?.status === 401
          && !original._retry
          && !isAuthAction
        ) {
          original._retry = true;
          const newAccess = await tryRefresh();
          if (newAccess) {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${newAccess}`;
            return instance(original);
          }
          clearLocalAuth();
        }

        if (
          error.response?.status === 403
          && ['GYM_PLAN_INACTIVE', 'GYM_BILLING_EXPIRED'].includes(error.response?.data?.error)
        ) {
          const message = error.response.data.message || 'Utente non attivo. Contatta la palestra per maggiori informazioni.';
          try {
            sessionStorage.setItem('login_message', message);
            sessionStorage.setItem('login_message_code', error.response.data.error);
          } catch (e) {}
          clearLocalAuth();
        }
        return Promise.reject(error);
      }
    );
  
    return instance;
  }, [token, tryRefresh, clearLocalAuth]);

  // Verify token on mount or token change
  useEffect(() => {
    let mounted = true;

    const verifyToken = async () => {
      if (!token) {
        // Prova refresh se abbiamo solo refresh_token
        const refreshed = await tryRefresh();
        if (!refreshed) {
          setLoading(false);
          return;
        }
      }

      try {
        const response = await api.post('/auth.php?action=verify');
        if (mounted && response.data?.user) {
          setCurrentUser(response.data.user);
        }
      } catch (err) {
        console.error('Token validation error:', err);
        if (mounted) {
          const refreshed = await tryRefresh();
          if (refreshed) {
            try {
              const response = await api.post('/auth.php?action=verify');
              if (mounted && response.data?.user) {
                setCurrentUser(response.data.user);
                setLoading(false);
                return;
              }
            } catch (_) {}
          }
          clearLocalAuth();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    verifyToken();
    return () => {
      mounted = false;
    };
  }, [token, api, tryRefresh, clearLocalAuth]);

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await api.post('/auth.php?action=login', {
        username,
        password,
        device_id: getDeviceId()
      });
      
      const access = response.data?.access_token || response.data?.token;
      const refresh = response.data?.refresh_token;
      const user = response.data?.user;

      if (!access) {
        throw new Error('Token mancante nella risposta di login');
      }

      persistTokens(access, refresh);
      setCurrentUser(user);
      
      return user;
    } catch (err) {
      // 🔒 Gestione specifica per accesso negato alla webapp
      if (err.response?.status === 403 && err.response?.data?.error === 'ACCESS_DENIED_WEBAPP') {
        const errorMessage = err.response.data.message;
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // 🔒 Gestione specifica per account sospeso
      if (err.response?.status === 403 && err.response?.data?.error === 'ACCOUNT_SUSPENDED') {
        const errorMessage = err.response.data.message || 'Account sospeso dalla palestra';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      // 🔒 Abbonamento palestra scaduto (trainer/membro) o piano disattivato
      if (err.response?.status === 403 && err.response?.data?.error === 'GYM_BILLING_EXPIRED') {
        const errorMessage = err.response.data.message
          || 'L\'abbonamento della palestra è scaduto. Contatta il gestore o assistenza@fitgymtrack.com.';
        setError(errorMessage);
        const e = new Error(errorMessage);
        e.loginCode = 'GYM_BILLING_EXPIRED';
        throw e;
      }
      if (err.response?.status === 403 && err.response?.data?.error === 'GYM_PLAN_INACTIVE') {
        const errorMessage = err.response.data.message || 'Utente non attivo. Contatta la palestra per maggiori informazioni.';
        setError(errorMessage);
        const e = new Error(errorMessage);
        e.loginCode = 'GYM_PLAN_INACTIVE';
        throw e;
      }
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Errore durante il login';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };
  
  const logout = async () => {
    try {
      const refresh = localStorage.getItem(REFRESH_KEY);
      if (token || refresh) {
        await api.post('/auth.php?action=logout', {
          ...(token ? { token } : {}),
          ...(refresh ? { refresh_token: refresh } : {}),
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearLocalAuth();
    }
  };

  const getAuthenticatedApi = () => api;

  const refreshCurrentUser = useCallback(async () => {
    if (!token && !localStorage.getItem(REFRESH_KEY)) {
      return null;
    }
    try {
      const response = await api.post('/auth.php?action=verify');
      if (response.data?.user) {
        setCurrentUser(response.data.user);
        return response.data.user;
      }
    } catch (err) {
      console.error('refreshCurrentUser:', err);
    }
    return null;
  }, [api, token]);

  const isAdmin = () => currentUser?.role_name === 'admin';
  
  // Funzione per verificare se l'utente è standalone
  const isStandalone = () => currentUser?.role_name === 'standalone';
  
  // Funzione per verificare se l'utente è gym
  const isGym = () => currentUser?.role_name === 'gym';
  
  // Funzione per verificare se l'utente è trainer
  const isTrainer = () => currentUser?.role_name === 'trainer';
  
  const hasRole = (roleName) => currentUser?.role_name === roleName;

  // WEBAPP-031: Piano palestra (start | premium | elite); utile per nascondere calendario/corsi
  const gymPlan = () => currentUser?.gym_plan || 'monthly';
  const isGymPlanStart = () => gymPlan() === 'start';
  const isGymPlanActive = () => currentUser?.gym_plan_active !== 0;

  const value = {
    currentUser,
    user: currentUser, // Alias per compatibilità
    loading,
    error,
    login,
    logout,
    isAdmin,
    isStandalone,
    isGym,
    isTrainer,
    hasRole,
    getAuthenticatedApi,
    refreshCurrentUser,
    gymPlan,
    isGymPlanStart,
    isGymPlanActive
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
